// The content and marketing calendar: every post, email, article, video and event on one calendar, with the
// campaigns running behind them. Month view to plan (an agenda on a phone), a board to move work along, a list to
// scan. Click a day to add something to it; drag a post to another day to reschedule it, or onto "No date yet" to take
// its date away; drag a card across the board to move it on.
import { useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CommandBar } from "../Shell";
import { ErrorBox } from "../ui";
import { useLoad } from "../hooks";
import {
  mk, iso, todayIso, shortDate, longDate, CHANNEL_LABEL, CONTENT_STATE_LABEL, CONTENT_FLOW,
  type Campaign, type Content, type ContentState, type Person,
} from "./mkApi";
import { em } from "./emApi";
import { reach } from "./reachApi";
import { LinkedInPost, PostFields, emptyPost, type PostDraft } from "./LinkedInPost";
import "../maintenance/mx.css";
import "./mk.css";
import "./em.css";
import "./reach.css";

// Weeks start on Sunday, as on most US calendars.
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const monthStart = (ym: string) => new Date(Number(ym.slice(0, 4)), Number(ym.slice(5, 7)) - 1, 1);
const ymOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
/** The six weeks shown for a month, Sunday first. */
function gridDays(ym: string) {
  const first = monthStart(ym);
  const start = new Date(first); start.setDate(1 - first.getDay());
  return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}
const isLate = (c: Content) => !!c.publishDate && c.publishDate < todayIso() && !["published", "cancelled"].includes(c.state);

const MailIcon = () => <svg className="mk-mail" width="11" height="11" viewBox="0 0 16 16" aria-hidden="true"><rect x="1.5" y="3.5" width="13" height="9" rx="1.5" fill="none" stroke="currentColor" strokeWidth="1.5" /><path d="M2 4.5 8 9l6-4.5" fill="none" stroke="currentColor" strokeWidth="1.5" /></svg>;
const clock = (at: string) => new Date(at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
/** When a scheduled email or LinkedIn post goes out, as a time of day. */
const sendTime = (c: Content) => (c.sendAt && c.emailState === "scheduled" ? clock(c.sendAt) : c.postAt && c.postStatus === "scheduled" ? clock(c.postAt) : null);
/** An email that has gone out, or a post that's on LinkedIn, stays on the day it went out. */
const fixed = (c: Content) => (!!c.emailNumber && ["sending", "sent"].includes(c.emailState ?? "")) || c.postStatus === "posting" || c.postStatus === "posted";
const tip = (c: Content) => `${c.title} · ${c.emailNumber ? `Email ${c.emailNumber}${sendTime(c) ? `, sends at ${sendTime(c)}` : fixed(c) ? ", already sent" : ""}`
  : c.postStatus === "scheduled" && sendTime(c) ? `LinkedIn post, goes out at ${sendTime(c)}` : c.postStatus === "posted" ? `On LinkedIn${c.postImpressions ? `, ${c.postImpressions.toLocaleString()} impressions` : ""}` : CHANNEL_LABEL[c.channel]} · ${CONTENT_STATE_LABEL[c.state]}${c.ownerName ? ` · ${c.ownerName}` : ""}`;
/** Phones get the month as an agenda: a grid seven days wide has no room for titles. */
function useNarrow(px = 700) {
  const q = `(max-width: ${px}px)`;
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && !!window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q); if (!m) return;
    const on = () => setNarrow(m.matches); on();
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, [q]);
  return narrow;
}

export function ChannelTag({ channel }: { channel: string }) {
  return <span className={`ch-tag ch ch-${channel}`}><span className="ch-dot" />{CHANNEL_LABEL[channel] ?? channel}</span>;
}
export function ContentChip({ state }: { state: ContentState }) {
  return <span className={`mx-chip ${state === "published" ? "ok" : state === "review" ? "warn" : ""}`}>{CONTENT_STATE_LABEL[state]}</span>;
}

export function CalendarPage() {
  const [sp, setSp] = useSearchParams();
  const view = (sp.get("view") ?? "month") as "month" | "board" | "list";
  const ym = sp.get("m") ?? ymOf(new Date());
  const [channel, setChannel] = useState(sp.get("channel") ?? "");
  const [campaign, setCampaign] = useState(sp.get("campaign") ?? "");
  const [mine, setMine] = useState(false);
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState<{ id?: string; date?: string } | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const nav = useNavigate();
  const days = useMemo(() => gridDays(ym), [ym]);
  // Month: the six weeks on screen plus ideas. Board and list: everything from a month back.
  const range = view === "month" ? { from: iso(days[0]), to: iso(days[41]) } : { from: iso(new Date(Date.now() - 31 * 864e5)), to: undefined };
  const list = useLoad(() => mk.content({ ...range, undated: true, channel: channel || undefined, campaign: campaign || undefined, owner: mine ? "me" : undefined }), [view, ym, channel, campaign, mine, tick]);
  const camps = useLoad(() => mk.campaigns(), [tick]);
  const people = useLoad(() => mk.people());
  const set = (k: string, v: string | null) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n, { replace: true }); };
  const shift = (n: number) => { const d = monthStart(ym); d.setMonth(d.getMonth() + n); set("m", ymOf(d)); };
  const move = async (id: string, b: Record<string, unknown>) => { setErr(null); try { await mk.updateContent(id, b); setTick((x) => x + 1); } catch (e) { setErr(e); } };
  const items = list.data ?? [];
  // An email opens in the email editor; everything else in the drawer.
  const open = (id: string) => { const c = items.find((x) => x.number === id); if (c?.emailNumber) nav(`/marketing/emails/${c.emailNumber}`); else setEditing({ id }); };
  const dated = items.filter((c) => c.publishDate);
  const ideas = items.filter((c) => !c.publishDate && c.state !== "cancelled");
  const status = list.data ? `${dated.filter((c) => c.state !== "cancelled").length} on the calendar · ${ideas.length} with no date` : "Loading…";

  return (
    <>
      <CommandBar status={status}>
        <button className="cmd primary" onClick={() => setEditing({})}>New content</button>
        <Link className="cmd" to="/marketing/emails?new=1">New email</Link>
      </CommandBar>
      <div className="page">
        <div className="page-title"><h1>Content calendar</h1><span className="sub">Posts, emails, articles, videos and events, with the campaigns behind them</span></div>
        <ErrorBox error={err ?? list.error} />
        <section className="card">
          <div className="mk-cal-bar">
            {view === "month" && <>
              <button className="icon-nav" aria-label="Previous month" onClick={() => shift(-1)}>‹</button>
              <button className="icon-nav" aria-label="Next month" onClick={() => shift(1)}>›</button>
              <h2>{monthStart(ym).toLocaleDateString(undefined, { month: "long", year: "numeric" })}</h2>
              <button className="btn" onClick={() => set("m", null)}>Today</button>
            </>}
            <span className="spacer" />
            <select className="select" style={{ width: "auto" }} aria-label="Channel" value={channel} onChange={(e) => setChannel(e.target.value)}>
              <option value="">All channels</option>{Object.entries(CHANNEL_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select className="select" style={{ width: "auto", maxWidth: 200 }} aria-label="Campaign" value={campaign} onChange={(e) => setCampaign(e.target.value)}>
              <option value="">All campaigns</option>{(camps.data ?? []).map((c) => <option key={c.id} value={c.number}>{c.name}</option>)}
            </select>
            <label className="muted" style={{ display: "inline-flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} />Mine</label>
            <div className="mk-seg" role="group" aria-label="View">
              {(["month", "board", "list"] as const).map((v) => <button key={v} aria-pressed={view === v} onClick={() => set("view", v === "month" ? null : v)}>{v === "month" ? "Month" : v === "board" ? "Board" : "List"}</button>)}
            </div>
          </div>
          {!list.data ? <div className="empty">Loading…</div>
            : view === "month" ? <Month days={days} ym={ym} items={dated} ideas={ideas} campaigns={camps.data ?? []} onOpen={open} onNew={(date) => setEditing({ date })} onMove={move} />
            : view === "board" ? <Board items={items} onOpen={open} onMove={move} />
            : <ListView items={items} onOpen={open} />}
        </section>
      </div>
      {editing && <ContentDrawer id={editing.id} date={editing.date} campaigns={camps.data ?? []} people={people.data ?? []} defaultCampaign={campaign}
        onClose={() => setEditing(null)} onSaved={() => setTick((x) => x + 1)} />}
    </>
  );
}

function useDrop(onDrop: (id: string) => void) {
  const [over, setOver] = useState(false);
  return {
    over,
    props: {
      onDragOver: (e: DragEvent) => { if (e.dataTransfer.types.includes("text/x-content")) { e.preventDefault(); setOver(true); } },
      onDragLeave: () => setOver(false),
      onDrop: (e: DragEvent) => { e.preventDefault(); setOver(false); const id = e.dataTransfer.getData("text/x-content"); if (id) onDrop(id); },
    },
  };
}
const dragProps = (c: Content) => ({ draggable: true, onDragStart: (e: DragEvent) => { e.dataTransfer.setData("text/x-content", c.number); e.dataTransfer.effectAllowed = "move"; } });

function Month({ days, ym, items, ideas, campaigns, onOpen, onNew, onMove }: {
  days: Date[]; ym: string; items: Content[]; ideas: Content[]; campaigns: Campaign[];
  onOpen: (id: string) => void; onNew: (date: string) => void; onMove: (id: string, b: Record<string, unknown>) => void;
}) {
  const today = todayIso();
  const byDay = useMemo(() => { const m = new Map<string, Content[]>(); for (const c of items) if (c.state !== "cancelled") m.set(c.publishDate!, [...(m.get(c.publishDate!) ?? []), c]); return m; }, [items]);
  const running = campaigns.filter((c) => c.startDate && ["planned", "active"].includes(c.state));
  const ideaDrop = useDrop((id) => onMove(id, { publishDate: null, ...(items.find((x) => x.number === id)?.state === "scheduled" ? { state: "drafting" } : {}) }));
  const narrow = useNarrow();
  const channels = [...new Set([...items, ...ideas].map((c) => c.channel))].sort((a, b) => Object.keys(CHANNEL_LABEL).indexOf(a) - Object.keys(CHANNEL_LABEL).indexOf(b));
  return (
    <div className="mk-cal-wrap">
      <div>
        {narrow ? <Agenda days={days.filter((d) => ymOf(d) === ym)} byDay={byDay} today={today} running={running} onOpen={onOpen} onNew={onNew} /> : (
        <div className="mk-month" role="grid" aria-label="Month">
          {DOW.map((d) => <div key={d} className="mk-dow" role="columnheader">{d}</div>)}
          {days.map((d, i) => {
            const k = iso(d);
            // A campaign runs as a bar across its days; its name shows on its first day and at the start of each week.
            const spans = running.filter((c) => c.startDate! <= k && (c.endDate ?? c.startDate!) >= k).map((c) => ({ c, named: c.startDate === k || i % 7 === 0 }));
            return <Day key={k} date={k} label={d.getDate()} out={ymOf(d) !== ym} today={k === today} items={byDay.get(k) ?? []} spans={spans} onOpen={onOpen} onNew={onNew}
              onDrop={(id) => onMove(id, { publishDate: k })} />;
          })}
        </div>)}
        {channels.length > 0 && <p className="mk-legend" aria-label="Colors">{channels.map((ch) => <ChannelTag key={ch} channel={ch} />)}</p>}
      </div>
      <aside className={`mk-ideas ${ideaDrop.over ? "drop" : ""}`} {...ideaDrop.props} aria-label="No date yet">
        <h3>No date yet <span className="muted" style={{ fontWeight: 400 }}>{ideas.length}</span></h3>
        <p>{narrow ? "Open one to give it a date." : "Drag one onto a day to give it a date."}</p>
        {ideas.map((c) => (
          <button key={c.id} className={`mk-item ch ch-${c.channel}`} {...dragProps(c)} onClick={() => onOpen(c.number)} title={`${c.title} · ${CHANNEL_LABEL[c.channel]}`}>
            <span className="t">{c.title}</span><span className="muted" style={{ fontSize: 11 }}>{CONTENT_STATE_LABEL[c.state]}</span>
          </button>))}
        {!ideas.length && <p>Everything has a date.</p>}
      </aside>
    </div>
  );
}

function Day({ date, label, out, today, items, spans, onOpen, onNew, onDrop }: {
  date: string; label: number; out: boolean; today: boolean; items: Content[]; spans: { c: Campaign; named: boolean }[];
  onOpen: (id: string) => void; onNew: (date: string) => void; onDrop: (id: string) => void;
}) {
  const drop = useDrop(onDrop);
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 4);
  return (
    <div className={`mk-day ${out ? "out" : ""} ${today ? "today" : ""} ${drop.over ? "drop" : ""}`} role="gridcell" aria-label={longDate(date)} {...drop.props}
      onClick={(e) => { if (!(e.target as HTMLElement).closest("button, a")) onNew(date); }} title={`Add something on ${longDate(date)}`}>
      <div className="mk-day-h"><span className="mk-dn">{label}</span><button className="mk-add" aria-label={`Add content on ${longDate(date)}`} onClick={() => onNew(date)}>+</button></div>
      {spans.map(({ c, named }) => <Link key={c.id} to={`/marketing/campaigns/${c.number}`} className={`mk-span ${c.state} ${named ? "" : "cont"}`} title={`${c.name} (campaign)`}>{named ? c.name : "\u00a0"}</Link>)}
      {shown.map((c) => (
        <button key={c.id} className={`mk-item ch ch-${c.channel} ${c.state === "published" ? "published" : ""} ${isLate(c) ? "late" : ""}`} {...(fixed(c) ? {} : dragProps(c))} onClick={() => onOpen(c.number)} title={tip(c)}>
          {c.emailNumber && <MailIcon />}<span className="t">{c.title}{sendTime(c) && <span className="tm"> · {sendTime(c)}</span>}</span>
        </button>))}
      {items.length > 4 && !all && <button className="mk-more" onClick={() => setAll(true)}>{items.length - 4} more</button>}
    </div>
  );
}

/** The month on a phone: each day that has something, with full titles; campaigns running that day above them. */
function Agenda({ days, byDay, today, running, onOpen, onNew }: {
  days: Date[]; byDay: Map<string, Content[]>; today: string; running: Campaign[]; onOpen: (id: string) => void; onNew: (date: string) => void;
}) {
  const shown = days.map((d) => iso(d)).filter((k) => k === today || (byDay.get(k) ?? []).length);
  if (!shown.length) return <div className="empty">Nothing this month. <button className="linkish" onClick={() => onNew(iso(days[0]))}>Add something</button></div>;
  return (
    <ol className="mk-agenda" aria-label="Month">
      {shown.map((k) => {
        const items = byDay.get(k) ?? [], camps = running.filter((c) => c.startDate! <= k && (c.endDate ?? c.startDate!) >= k);
        return (
          <li key={k} className={k === today ? "today" : ""}>
            <div className="mk-ag-h"><span>{k === today ? "Today · " : ""}{longDate(k)}</span><button className="mk-add" aria-label={`Add content on ${longDate(k)}`} onClick={() => onNew(k)}>+</button></div>
            {camps.map((c) => <Link key={c.id} to={`/marketing/campaigns/${c.number}`} className={`mk-span ${c.state}`}>{c.name}</Link>)}
            {items.map((c) => (
              <button key={c.id} className={`mk-item ch ch-${c.channel} ${c.state === "published" ? "published" : ""} ${isLate(c) ? "late" : ""}`} onClick={() => onOpen(c.number)} title={tip(c)}>
                {c.emailNumber && <MailIcon />}<span className="t">{c.title}{sendTime(c) && <span className="tm"> · {sendTime(c)}</span>}</span>
                <span className="mk-ag-s">{CONTENT_STATE_LABEL[c.state]}</span>
              </button>))}
            {!items.length && <p className="muted" style={{ margin: "2px 0", fontSize: 13 }}>Nothing today.</p>}
          </li>
        );
      })}
    </ol>
  );
}

function Board({ items, onOpen, onMove }: { items: Content[]; onOpen: (id: string) => void; onMove: (id: string, b: Record<string, unknown>) => void }) {
  return (
    <div className="mk-board">
      {CONTENT_FLOW.map((s) => <Column key={s} state={s} items={items.filter((c) => c.state === s)} onOpen={onOpen} onDrop={(id) => { if (!items.find((c) => c.number === id)?.emailNumber) onMove(id, { state: s }); }} />)}
    </div>
  );
}
function Column({ state, items, onOpen, onDrop }: { state: ContentState; items: Content[]; onOpen: (id: string) => void; onDrop: (id: string) => void }) {
  const drop = useDrop(onDrop);
  const sorted = [...items].sort((a, b) => (a.publishDate ?? "9999").localeCompare(b.publishDate ?? "9999"));
  return (
    <section className={`mk-col ${drop.over ? "drop" : ""}`} {...drop.props} aria-label={CONTENT_STATE_LABEL[state]}>
      <div className="mk-col-h">{CONTENT_STATE_LABEL[state]}<span className="n">{items.length}</span></div>
      <div className="mk-col-b">
        {sorted.map((c) => (
          <button key={c.id} className={`mk-card ch ch-${c.channel}`} {...(c.emailNumber ? {} : dragProps(c))} onClick={() => onOpen(c.number)}>
            <span className="t">{c.emailNumber && <MailIcon />} {c.title}</span>
            <span className="m"><ChannelTag channel={c.channel} /><span className={isLate(c) ? "late" : ""}>{c.publishDate ? shortDate(c.publishDate) : "No date"}</span></span>
            {(c.campaignName || c.ownerName) && <span className="m"><span>{c.campaignName ?? ""}</span><span>{c.ownerName ?? ""}</span></span>}
          </button>))}
        {!items.length && <p className="muted" style={{ margin: 4, fontSize: 12 }}>Nothing here.</p>}
      </div>
    </section>
  );
}

function ListView({ items, onOpen }: { items: Content[]; onOpen: (id: string) => void }) {
  const [q, setQ] = useState("");
  const term = q.trim().toLowerCase();
  const rows = [...items].filter((c) => !term || [c.title, c.number, c.campaignName, c.ownerName, CHANNEL_LABEL[c.channel], CONTENT_STATE_LABEL[c.state]].some((x) => x?.toLowerCase().includes(term)))
    .sort((a, b) => (a.publishDate ?? "9999").localeCompare(b.publishDate ?? "9999"));
  const filter = <div className="mk-list-filter"><input className="input" type="search" aria-label="Filter content" placeholder="Filter content" value={q} onChange={(e) => setQ(e.target.value)} /></div>;
  if (!items.length) return <div className="empty">No content yet.</div>;
  if (!rows.length) return <>{filter}<div className="empty">Nothing matches "{q.trim()}".</div></>;
  return (
    <>{filter}
    <div className="table-wrap"><table className="dgrid mk-list">
      <thead><tr><th className="mk-list-dc">Date</th><th>Content</th><th>Channel</th><th>Status</th><th className="mx-hide-m">Campaign</th><th className="mx-hide-m">Owner</th></tr></thead>
      <tbody>{rows.map((c) => (
        <tr key={c.id}>
          <td style={{ whiteSpace: "nowrap" }} className={`mk-list-dc${isLate(c) ? " mx-late" : ""}`}>{c.publishDate ? longDate(c.publishDate) : <span className="muted">No date</span>}</td>
          <td className="title"><span className="mk-list-d">{c.publishDate ? longDate(c.publishDate) : "No date"}</span><button className="linkish" style={{ fontSize: 14, fontWeight: 600, textAlign: "left" }} onClick={() => onOpen(c.number)}>{c.title}</button><span className="sub">{c.number}</span></td>
          <td><ChannelTag channel={c.channel} /></td>
          <td><ContentChip state={c.state} /></td>
          <td className="mx-hide-m">{c.campaignNumber ? <Link to={`/marketing/campaigns/${c.campaignNumber}`}>{c.campaignName}</Link> : <span className="muted">–</span>}</td>
          <td className="mx-hide-m">{c.ownerName ?? <span className="muted">–</span>}</td>
        </tr>))}
      </tbody>
    </table></div></>
  );
}

/** Create or edit one piece of content, in a drawer over the calendar. */
export function ContentDrawer({ id, date, campaigns, people, defaultCampaign, onClose, onSaved }: {
  id?: string; date?: string; campaigns: Campaign[]; people: Person[]; defaultCampaign?: string; onClose: () => void; onSaved: () => void;
}) {
  const one = useLoad(() => (id ? mk.contentOne(id) : Promise.resolve(null)), [id]);
  const [v, setV] = useState<Record<string, string>>({ title: "", channel: "linkedin", publishDate: date ?? "", campaign: defaultCampaign ?? "", ownerId: "", brief: "", url: "" });
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [post, setPost] = useState<PostDraft | null>(null);
  const c = one.data;
  // A LinkedIn post that's scheduled or out sets the item's stage; the post's own buttons move it.
  const postLed = !!c && ["scheduled", "posting", "posted"].includes(c.postStatus ?? "");
  // Fields follow the item as it reloads (scheduling a post moves its date), except the ones being edited here; Save
  // sends only those, so it never puts back a value that changed elsewhere.
  const touched = useRef(new Set<string>());
  useEffect(() => { touched.current = new Set(); }, [c?.id]);
  useEffect(() => {
    if (!c) return;
    const from: Record<string, string> = { title: c.title, channel: c.channel, publishDate: c.publishDate ?? "", campaign: c.campaignNumber ?? "", ownerId: c.ownerId ?? "", brief: c.brief, url: c.url ?? "" };
    setV((cur) => Object.fromEntries(Object.entries(from).map(([k, x]) => [k, touched.current.has(k) ? cur[k] : x])));
  }, [c]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  const f = (k: string) => (e: { target: { value: string } }) => { touched.current.add(k); setV({ ...v, [k]: e.target.value }); };
  const payload = () => {
    const all: Record<string, unknown> = { title: v.title, channel: v.channel, publishDate: v.publishDate || null, campaign: v.campaign || null, ownerId: v.ownerId || null, brief: v.brief, url: v.url || null };
    return id ? Object.fromEntries(Object.entries(all).filter(([k]) => touched.current.has(k))) : all;
  };
  const save = async (e?: FormEvent, extra: Record<string, unknown> = {}) => {
    e?.preventDefault(); setBusy(true); setErr(null);
    try {
      const saved = id ? await mk.updateContent(id, { ...payload(), ...extra }) : await mk.createContent({ ...payload(), ...extra });
      // The post is saved with the item, unless it's already on LinkedIn.
      if (v.channel === "linkedin" && post && (post.text.trim() || c?.postStatus) && !["posting", "posted"].includes(c?.postStatus ?? ""))
        await reach.savePost(saved.number, { text: post.text, linkUrl: post.linkUrl.trim() || null, imageUrl: post.imageUrl.trim() || null });
      onSaved(); onClose();
    }
    catch (x) { setErr(x); setBusy(false); }
  };
  const stage = async (s: ContentState) => { if (!id) return; setBusy(true); setErr(null); try { await mk.updateContent(id, { ...payload(), state: s }); onSaved(); await one.reload(); } catch (x) { setErr(x); } setBusy(false); };
  const at = c ? CONTENT_FLOW.indexOf(c.state) : -1;
  const nav = useNavigate();
  // Email on the calendar: write it in the email editor, which keeps this place on the calendar in step.
  const writeEmail = async () => {
    setBusy(true); setErr(null);
    try {
      const contentNo = id ?? (await mk.createContent({ ...payload(), channel: "email" })).number;
      const e = await em.create({ content: contentNo, starter: "announcement" });
      nav(`/marketing/emails/${e.number}`);
    } catch (x) { setErr(x); setBusy(false); }
  };
  return (
    <div className="peek-wrap mk-drawer" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="peek" role="dialog" aria-modal="true" aria-labelledby="cd-h" onSubmit={save}>
        <div className="peek-h">
          <div style={{ minWidth: 0 }}><h2 id="cd-h">{id ? (c?.title ?? "Loading…") : "New content"}</h2>
            <div className="peek-sub">{c ? <>{c.number} · <span className="peek-status">{CONTENT_STATE_LABEL[c.state]}</span></> : date ? `For ${longDate(date)}` : "An idea, a post, an email, an article, a video or an event"}</div></div>
          <button type="button" className="icon-btn" aria-label="Close" onClick={onClose}><svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 3l10 10M13 3L3 13" stroke="currentColor" strokeWidth="1.6" /></svg></button>
        </div>
        <div className="peek-body">
          <ErrorBox error={err ?? one.error} />
          {c?.emailNumber && (
            <div className="em-banner test" style={{ marginBottom: 14 }}><div>This is email <b>{c.emailNumber}</b>{c.emailState === "scheduled" && c.sendAt ? `, going out ${new Date(c.sendAt).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : c.emailState ? `, ${c.emailState}` : ""}. Write, schedule and send it in the email.</div>
              <Link className="btn" to={`/marketing/emails/${c.emailNumber}`}>Open the email</Link></div>)}
          {c && !c.emailNumber && !postLed && c.state !== "cancelled" && (
            <div className="mk-flow" role="group" aria-label="Stage">
              {CONTENT_FLOW.map((s, i) => <button type="button" key={s} disabled={busy} className={i < at ? "done" : ""} aria-current={s === c.state} onClick={() => stage(s)}>{CONTENT_STATE_LABEL[s]}</button>)}
            </div>)}
          <div className="field"><label htmlFor="cd-t">Title</label><input id="cd-t" className="input" required autoFocus={!id} value={v.title} onChange={f("title")} placeholder="Case study: how Acme cut scrap by 30%" /></div>
          <div className="form-grid">
            <div className="field"><label htmlFor="cd-c">Channel</label><select id="cd-c" className="select" value={v.channel} onChange={f("channel")}>{Object.entries(CHANNEL_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
            <div className="field"><label htmlFor="cd-d">Publish date</label><input id="cd-d" type="date" className="input" value={v.publishDate} onChange={f("publishDate")} /><span className="hint">Leave empty if it doesn't have a date yet.</span></div>
            <div className="field"><label htmlFor="cd-cmp">Campaign</label><select id="cd-cmp" className="select" value={v.campaign} onChange={f("campaign")}><option value="">None</option>{campaigns.filter((x) => x.state !== "cancelled" || x.number === v.campaign).map((x) => <option key={x.id} value={x.number}>{x.name}</option>)}</select></div>
            <div className="field"><label htmlFor="cd-o">Owner</label><select id="cd-o" className="select" value={v.ownerId} onChange={f("ownerId")}><option value="">{id ? "Nobody" : "Me"}</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></div>
          </div>
          {v.channel === "linkedin" && (id && c
            ? <LinkedInPost content={c.number} publishDate={v.publishDate} v={post} onDraft={setPost} onChanged={() => { onSaved(); one.reload(); }} />
            : !id && <div className="rc-post"><h3><span className="rc-li" aria-hidden="true">in</span>LinkedIn post</h3><p className="who">Write it now or later. Schedule it or post it once the item is saved.</p><PostFields v={post ?? emptyPost} onChange={setPost} /></div>)}
          <div className="field"><label htmlFor="cd-b">Brief</label><textarea id="cd-b" className="textarea" rows={6} value={v.brief} onChange={f("brief")} placeholder="Audience, key message, call to action, what it links to" style={{ height: "auto", padding: "6px 10px" }} /></div>
          {id && <div className="field"><label htmlFor="cd-u">Link</label><input id="cd-u" className="input" value={v.url} onChange={f("url")} placeholder="Where it lives once it's published, or the draft" /></div>}
          {c && c.history.length > 0 && (<>
            <h3 style={{ fontSize: 13, margin: "8px 0 6px" }}>History</h3>
            <ul className="mx-hist">{c.history.slice().reverse().map((h, i) => <li key={i}><span className="when">{new Date(h.at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</span><span>{h.reason === "created" ? `Created as ${CONTENT_STATE_LABEL[h.to as ContentState] ?? h.to}` : `${CONTENT_STATE_LABEL[h.from as ContentState] ?? h.from ?? "–"} → ${CONTENT_STATE_LABEL[h.to as ContentState] ?? h.to}`}<span className="muted"> · {h.by}</span></span></li>)}</ul>
          </>)}
        </div>
        <div className="peek-actions">
          {v.channel === "email" && !c?.emailNumber && <button type="button" className="btn primary" disabled={busy || !v.title.trim()} onClick={writeEmail}>Write the email</button>}
          <button className={`btn ${v.channel === "email" && !c?.emailNumber ? "" : "primary"}`} disabled={busy || !v.title.trim()}>{id ? "Save" : v.publishDate ? "Add to calendar" : "Save idea"}</button>
          <button type="button" className="btn" onClick={onClose}>Close</button>
          <span style={{ flex: 1 }} />
          {c && !c.emailNumber && !postLed && c.state !== "cancelled" && c.state !== "published" && <button type="button" className="btn" disabled={busy} onClick={() => save(undefined, { state: "cancelled" })}>Mark cancelled</button>}
          {c && !c.emailNumber && c.state === "cancelled" && <button type="button" className="btn" disabled={busy} onClick={() => save(undefined, { state: "idea" })}>Restore</button>}
        </div>
      </form>
    </div>
  );
}
